import type { InteractionSubtopicInput, InteractionTopicInput } from "../queries";
import { OTHER_TOPIC } from "../queries";

export type TopicValidationErrors = {
  topicErrors: Record<number, string | undefined>;
  topicDescriptionErrors: Record<number, string | undefined>;
  subtopicErrors: Record<number, string | undefined>;
  subtopicDescriptionErrors: Record<string, string | undefined>;
  formError?: string;
};

export const OTHER_TOPIC_WARNING = `"${OTHER_TOPIC}" is always shown — remove this row.`;
export const OTHER_SUBTOPIC_WARNING = `"${OTHER_TOPIC}" is added automatically — remove this subtopic.`;
export const MIN_TOPICS_ERROR = `Add at least one topic — "${OTHER_TOPIC}" alone is not enough.`;
export const DESCRIPTION_MAX = 500;
export const DESCRIPTION_TOO_LONG = `Keep descriptions to ${DESCRIPTION_MAX} characters or fewer.`;

export function subtopicErrorKey(topicIndex: number, subtopicIndex: number): string {
  return `${topicIndex}:${subtopicIndex}`;
}

// Tracks the in-flight suggestion by stable row identity (not render index)
// so add/remove shifts mid-request can't misattribute the loading state.
// Lives here (not in the page) so the editor doesn't import the page module.
export function suggestionKeyFor(topicKey: string | undefined, subtopicKey: string | null | undefined): string {
  return subtopicKey == null
    ? `topic:${topicKey ?? "?"}:description`
    : `topic:${topicKey ?? "?"}:sub:${subtopicKey ?? "?"}`;
}

let rowKeyCounter = 0;

// Client-only stable identity for editable rows. Index keys break when rows
// are inserted or removed (React reuses row state for a different subtopic),
// and editable names remount on every keystroke — so edit state carries an
// opaque key assigned at load/creation and stripped before serialization.
export function createRowKey(prefix: string): string {
  rowKeyCounter += 1;
  return `${prefix}-${rowKeyCounter}`;
}

// Fills missing row keys in place-shaped data (server echoes, test fixtures)
// while preserving existing keys so row identity survives echo merges.
export function withRowKeys(topics: InteractionTopicInput[]): InteractionTopicInput[] {
  return topics.map((topic) => ({
    ...topic,
    key: topic.key ?? createRowKey("topic"),
    subtopics: topic.subtopics.map((sub) => ({ ...sub, key: sub.key ?? createRowKey("sub") }))
  }));
}

function descriptionTooLong(description: string | null | undefined): boolean {
  return (description ?? "").length > DESCRIPTION_MAX;
}

export function validateInteractionTopics(topics: InteractionTopicInput[]): TopicValidationErrors {
  const topicErrors: Record<number, string | undefined> = {};
  const topicDescriptionErrors: Record<number, string | undefined> = {};
  const subtopicErrors: Record<number, string | undefined> = {};
  const subtopicDescriptionErrors: Record<string, string | undefined> = {};

  const seenTopics = new Map<string, number>();
  topics.forEach((topic, topicIndex) => {
    const name = topic.name.trim();
    if (!name) {
      topicErrors[topicIndex] = "Topic name is required.";
    } else if (name.toLowerCase() === OTHER_TOPIC.toLowerCase() && !isTopicLocked(topics, topicIndex)) {
      // A row renamed to Other would silently dedupe away on save — warn to
      // remove it instead of showing a confusing duplicate error.
      topicErrors[topicIndex] = OTHER_TOPIC_WARNING;
    } else {
      const key = name.toLowerCase();
      if (seenTopics.has(key)) {
        topicErrors[topicIndex] = "Duplicate topic.";
        const firstIndex = seenTopics.get(key);
        if (firstIndex !== undefined && !topicErrors[firstIndex]) {
          topicErrors[firstIndex] = "Duplicate topic.";
        }
      } else {
        seenTopics.set(key, topicIndex);
      }
    }

    if (descriptionTooLong(topic.description)) {
      topicDescriptionErrors[topicIndex] = DESCRIPTION_TOO_LONG;
    }

    // Subtopics edit as structured rows (name + description). Blank names are
    // ignored on save (filtered at serialization, never while typing); only
    // the first duplicate is reported. The canonical trailing Other is a
    // save-time concern (the backend appends it), so a typed Other warns here
    // instead of duplicating.
    const seenSubtopics = new Set<string>();
    topic.subtopics.forEach((subtopic, subtopicIndex) => {
      const subName = subtopic.name.trim();
      if (!subName) return;
      if (descriptionTooLong(subtopic.description)) {
        subtopicDescriptionErrors[subtopicErrorKey(topicIndex, subtopicIndex)] = DESCRIPTION_TOO_LONG;
      }
      const lower = subName.toLowerCase();
      if (lower === OTHER_TOPIC.toLowerCase()) {
        subtopicErrors[topicIndex] = OTHER_SUBTOPIC_WARNING;
        return;
      }
      if (seenSubtopics.has(lower)) {
        subtopicErrors[topicIndex] = `Duplicate subtopic "${subName}".`;
        return;
      }
      seenSubtopics.add(lower);
    });
  });

  const errors: TopicValidationErrors = {
    topicErrors,
    topicDescriptionErrors,
    subtopicErrors,
    subtopicDescriptionErrors
  };
  const realTopics = topics.filter(
    (topic, topicIndex) => !isTopicLocked(topics, topicIndex) && topic.name.trim().length > 0
  );
  if (realTopics.length === 0) {
    errors.formError = MIN_TOPICS_ERROR;
  }

  return errors;
}

export function hasValidationErrors(errors: TopicValidationErrors): boolean {
  return (
    Boolean(errors.formError) ||
    Object.values(errors.topicErrors).some(Boolean) ||
    Object.values(errors.topicDescriptionErrors).some(Boolean) ||
    Object.values(errors.subtopicErrors).some(Boolean) ||
    Object.values(errors.subtopicDescriptionErrors).some(Boolean)
  );
}

function normalizedDescription(description: string | null | undefined): string {
  return (description ?? "").trim();
}

function normalizedSubtopics(lines: InteractionSubtopicInput[]): InteractionSubtopicInput[] {
  return lines
    .filter((entry) => entry.name.trim().length > 0)
    .map((entry) => ({ name: entry.name.trim(), description: normalizedDescription(entry.description) }));
}

function subtopicsEqual(a: InteractionSubtopicInput[], b: InteractionSubtopicInput[]): boolean {
  const aSubs = normalizedSubtopics(a);
  const bSubs = normalizedSubtopics(b);
  return (
    aSubs.length === bSubs.length &&
    aSubs.every((sub, subIndex) => sub.name === bSubs[subIndex].name && sub.description === bSubs[subIndex].description)
  );
}

export function topicsEqual(a: InteractionTopicInput[], b: InteractionTopicInput[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((topic, index) => {
    const other = b[index];
    if (
      topic.name.trim() !== other.name.trim() ||
      normalizedDescription(topic.description) !== normalizedDescription(other.description) ||
      (topic.code ?? null) !== (other.code ?? null) ||
      (topic.defaultPrivate ?? null) !== (other.defaultPrivate ?? null)
    ) {
      return false;
    }
    return subtopicsEqual(topic.subtopics, other.subtopics);
  });
}

export type TopicFieldChanges = Record<number, { name: boolean; description: boolean; subtopics: boolean }>;

// Per-field unsaved-change map for blue changed-text: topic indexes pair to
// the saved list (same pairing rules as diffSavedTopics), with subtopic rows
// diffed individually so only edited rows turn blue.
export type ChangedSubtopicFields = Record<number, { name: boolean; description: boolean }>;
export type ChangedTopicFields = Record<
  number,
  { name: boolean; description: boolean; subtopics: ChangedSubtopicFields }
>;

function pairSavedTopic(
  saved: InteractionTopicInput[],
  current: InteractionTopicInput[],
  topic: InteractionTopicInput,
  index: number
): InteractionTopicInput | undefined {
  // Prefer stable row identity: a renamed row keeps its key, so only the
  // name flags — never a phantom add/remove pair. Fall back to names when
  // keys miss: the saved snapshot is re-keyed on every save echo while
  // edited rows keep their keys, so post-save rows pair by name.
  if (topic.key !== undefined) {
    const byKey = saved.find((entry) => entry.key !== undefined && entry.key === topic.key);
    if (byKey) return byKey;
  }
  const savedByName = new Map(saved.map((entry) => [entry.name.trim().toLowerCase(), entry]));
  const key = topic.name.trim().toLowerCase();
  const direct = savedByName.get(key);
  if (direct) return direct;
  // A rename pairs with the same-index row when that old name vanished, so
  // unchanged subtopics don't misflag. Only when lengths match — otherwise
  // indexes shifted and the row is treated as new.
  if (saved.length === current.length) {
    const candidate = saved[index];
    if (candidate) {
      const currentNames = new Set(current.map((entry) => entry.name.trim().toLowerCase()));
      if (!currentNames.has(candidate.name.trim().toLowerCase())) return candidate;
    }
  }
  return undefined;
}

function diffChangedSubtopics(
  saved: InteractionSubtopicInput[],
  current: InteractionSubtopicInput[]
): ChangedSubtopicFields {
  const savedByKey = new Map(
    saved.filter((entry) => entry.key !== undefined).map((entry) => [entry.key as string, entry])
  );
  const savedByName = new Map(saved.map((entry) => [entry.name.trim().toLowerCase(), entry]));
  const changes: ChangedSubtopicFields = {};
  current.forEach((row, index) => {
    const key = row.name.trim().toLowerCase();
    // Key match first (precise renames), name fallback second (post-save
    // snapshots carry fresh keys while edited rows keep theirs).
    const prev =
      (row.key !== undefined ? savedByKey.get(row.key) : undefined) ?? (key ? savedByName.get(key) : undefined);
    if (!prev) {
      changes[index] = { name: true, description: normalizedDescription(row.description).length > 0 };
      return;
    }
    const nameChanged = prev.name.trim() !== row.name.trim();
    const descriptionChanged = normalizedDescription(prev.description) !== normalizedDescription(row.description);
    if (nameChanged || descriptionChanged) {
      changes[index] = { name: nameChanged, description: descriptionChanged };
    }
  });
  return changes;
}

// Diffs current edit state against the saved snapshot for blue changed-text.
// New topics flag every field (blue on empty inputs is invisible); paired
// topics flag only edited names, descriptions, and subtopic rows.
export function diffChangedTopics(
  saved: InteractionTopicInput[],
  current: InteractionTopicInput[]
): ChangedTopicFields {
  const changes: ChangedTopicFields = {};
  current.forEach((topic, index) => {
    const prev = pairSavedTopic(saved, current, topic, index);
    if (!prev) {
      const subtopics: ChangedSubtopicFields = {};
      topic.subtopics.forEach((row, subIndex) => {
        subtopics[subIndex] = { name: true, description: normalizedDescription(row.description).length > 0 };
      });
      changes[index] = { name: true, description: true, subtopics };
      return;
    }
    const nameChanged = prev.name.trim() !== topic.name.trim();
    const descriptionChanged = normalizedDescription(prev.description) !== normalizedDescription(topic.description);
    const subtopics = diffChangedSubtopics(prev.subtopics, topic.subtopics);
    if (nameChanged || descriptionChanged || Object.keys(subtopics).length > 0) {
      changes[index] = { name: nameChanged, description: descriptionChanged, subtopics };
    }
  });
  return changes;
}

// Diffs a sent save payload against the pre-save snapshot, keyed by the
// payload's indexes, so save confirmations can land on exactly the fields
// that changed. Names pair case-insensitively (both sides passed validation,
// so names are unique); a new name pairs with the same-index row when that
// old name vanished (a rename) so unchanged subtopic lines don't misflag.
// The same-index fallback only runs when the list length is unchanged — a
// delete in the same save shifts indexes, so the new name is treated as an
// add instead (the delete toast covers the removal).
export function diffSavedTopics(before: InteractionTopicInput[], after: InteractionTopicInput[]): TopicFieldChanges {
  const beforeByName = new Map(before.map((topic) => [topic.name.trim().toLowerCase(), topic]));
  const afterNames = new Set(after.map((topic) => topic.name.trim().toLowerCase()));
  const changes: TopicFieldChanges = {};
  after.forEach((topic, index) => {
    const key = topic.name.trim().toLowerCase();
    let prev = beforeByName.get(key);
    if (!prev && before.length === after.length) {
      const candidate = before[index];
      if (candidate && !afterNames.has(candidate.name.trim().toLowerCase())) {
        prev = candidate;
      }
    }
    if (!prev) {
      changes[index] = {
        name: true,
        description: normalizedDescription(topic.description).length > 0,
        subtopics: normalizedSubtopics(topic.subtopics).length > 0
      };
      return;
    }
    const nameChanged = prev.name.trim() !== topic.name.trim();
    const descriptionChanged = normalizedDescription(prev.description) !== normalizedDescription(topic.description);
    const subtopicsChanged = !subtopicsEqual(prev.subtopics, topic.subtopics);
    if (nameChanged || descriptionChanged || subtopicsChanged) {
      changes[index] = { name: nameChanged, description: descriptionChanged, subtopics: subtopicsChanged };
    }
  });
  return changes;
}

// Merges a server echo into local edit state without dropping in-progress
// whitespace: when the echo matches local content modulo trimming and blank
// rows, local raw text wins (so a blank row being typed survives the echo
// that strips it) while server-owned metadata is still absorbed (otherwise a
// derived flag would read dirty forever and resave in a loop). Anything
// structurally different takes the echo whole.
export function mergeEchoTopics(
  localTopics: InteractionTopicInput[],
  echoTopics: InteractionTopicInput[]
): InteractionTopicInput[] {
  if (localTopics.length !== echoTopics.length) return withRowKeys(echoTopics);
  const allMatch = localTopics.every((topic, index) => topicTextEqual(topic, echoTopics[index]));
  if (!allMatch) return withRowKeys(echoTopics);
  return withRowKeys(
    echoTopics.map((echo, index) => ({
      ...echo,
      key: localTopics[index].key,
      name: localTopics[index].name,
      description: localTopics[index].description,
      subtopics: localTopics[index].subtopics
    }))
  );
}

function topicTextEqual(a: InteractionTopicInput, b: InteractionTopicInput): boolean {
  if (a.name.trim() !== b.name.trim()) return false;
  if (normalizedDescription(a.description) !== normalizedDescription(b.description)) return false;
  return subtopicsEqual(a.subtopics, b.subtopics);
}

export function isOtherTopicName(name: string): boolean {
  return name.trim() === OTHER_TOPIC;
}

// The canonical Other row is always last (the editor inserts before it and the
// backend moves it last). Locking only the last Other keeps a row renamed to
// Other editable so the author can fix it instead of freezing.
export function isTopicLocked(topics: InteractionTopicInput[], topicIndex: number): boolean {
  return topicIndex === topics.length - 1 && isOtherTopicName(topics[topicIndex]?.name ?? "");
}
